import { Directive, Input, ComponentRef, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TooltipComponent } from './tooltip/tooltip.component';
import {
    OverlayRef,
    Overlay,
    OverlayPositionBuilder,
    ConnectionPositionPair,
    ConnectedPosition
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

/**
 * Tooltips whose overlay is currently attached, shared by every instance of this build
 *
 * @description Lets an opening tooltip dismiss the ones a broken hover chain could not close (host
 * removed by an *ngIf, row moved by an *ngFor, HTML5 drag, scroll), and drives the lazy scroll
 * listener below. Module-scoped on purpose: no @Injectable and no DI, so the behaviour is identical
 * in the applications that consume this library - one of which loads both the source and the built
 * copy in a single injector, where a `providedIn: 'root'` service would exist twice.
 */
const openTooltips = new Set<TooltipDirective>();

/** Removes the shared scroll listener; null while no tooltip is open. */
let removeScrollListener: (() => void) | null = null;

/**
 * Closes every open tooltip as soon as anything scrolls
 *
 * @description Boundary events are movement-driven, so scrolling a list under a motionless cursor
 * leaves the tooltip attached and frozen (no scroll strategy is configured, hence
 * NoopScrollStrategy). A CAPTURE-phase listener on `document` is used because element `scroll`
 * events do not bubble but do traverse the capture phase, so this sees inner `overflow: auto`
 * containers such as the assignment list - which a CDK scroll strategy cannot, since it only
 * observes registered `cdkScrollable` elements. Unlike a `wheel` handler it also covers keyboard
 * scrolling, scrollbar dragging, find-in-page and programmatic `scrollIntoView`.
 */
function onDocumentScroll(): void {
    openTooltips.forEach((tooltip) => tooltip.hide());
}

/**
 * Installs the shared scroll listener, once, when the first tooltip opens
 */
function startScrollWatch(): void {
    if (removeScrollListener) {
        return;
    }
    document.addEventListener('scroll', onDocumentScroll, { capture: true, passive: true });
    removeScrollListener = () =>
        document.removeEventListener('scroll', onDocumentScroll, { capture: true });
}

/**
 * Removes the shared scroll listener as soon as the last tooltip closes, so an idle page keeps none
 */
function stopScrollWatchIfIdle(): void {
    if (removeScrollListener && openTooltips.size === 0) {
        removeScrollListener();
        removeScrollListener = null;
    }
}

@Directive({
    selector: '[tooltip]',
    standalone: false
})
export class TooltipDirective implements OnInit, OnDestroy {
    @Input('tooltip') text = '';
    @Input() tooltipColor = '#000';
    @Input() tooltipTextColor = '#fff';
    @Input() tooltipPosition = 'left'; // above | below | right | left | above-right | below-right
    //  if false : no arrow is displayed
    @Input() hasArrow = true;
    tooltipPortal!: ComponentPortal<TooltipComponent>;

    private overlayRef!: OverlayRef;

    constructor(
        private overlayPositionBuilder: OverlayPositionBuilder,
        private elementRef: ElementRef,
        private overlay: Overlay
    ) {}

    @HostListener('mouseover', ['$event']) show(event?: MouseEvent) {
        if (!this.overlayRef) {
            return;
        }
        this.dismissStaleTooltips(event ? event.target : null);
        if (!this.overlayRef.hasAttached()) {
            // Create tooltip portal
            this.tooltipPortal = new ComponentPortal(TooltipComponent);

            // Attach tooltip portal to overlay
            const tooltipRef: ComponentRef<TooltipComponent> = this.overlayRef.attach(this.tooltipPortal);
            /**
             * Makes the overlay pane transparent to the pointer, after every attach
             *
             * @description The CDK pane is `pointer-events: auto`, so a tooltip whose exit event
             * never arrived swallows every click over its own box. A tooltip has nothing
             * interactive inside it (`.tooltip-container` is already `pointer-events: none`), so
             * the pane never needs to receive the pointer. It has to be re-applied on every attach
             * because `OverlayRef.attach()` clears the inline value (`_togglePointerEvents(true)`),
             * and it cannot come from the component stylesheet because the pane lives outside the
             * component's emulated encapsulation - which is also why the
             * `@use '@angular/cdk/overlay-prebuilt.css'` in tooltip.component.scss is dead code.
             */
            this.overlayRef.overlayElement.style.pointerEvents = 'none';
            // Pass content to tooltip component instance
            tooltipRef.instance.text = this.text;
            tooltipRef.instance.tooltipColor = this.tooltipColor;
            tooltipRef.instance.hasArrow = this.hasArrow;
            tooltipRef.instance.tooltipPosition = this.tooltipPosition;
            tooltipRef.instance.tooltipTextColor = this.tooltipTextColor;

            openTooltips.add(this);
            startScrollWatch();
        }
    }

    @HostListener('click')
    @HostListener('mousedown')
    @HostListener('mouseout')
    hide() {
        openTooltips.delete(this);
        stopScrollWatchIfIdle();
        this.overlayRef?.detach();
    }

    /**
     * Dismisses every other open tooltip that no longer belongs to the hovered chain
     *
     * @description An ancestor host still contains the hovered target, so nested tooltips keep
     * exactly their current behaviour; a tooltip anchored to a row the pointer has left, or whose
     * host is no longer in the document, is detached. Costs nothing when idle: no listener is
     * involved, the check runs inside the `mouseover` handler that already fires, and the set almost
     * always holds 0 to 2 entries. `nativeElement` is typed `Element` and not `HTMLElement` because
     * some hosts are SVG shapes (the quiz editor navigation puts `[tooltip]` on `<circle>`).
     *
     * @param target EventTarget the pointer is currently over, or null
     */
    private dismissStaleTooltips(target: EventTarget | null): void {
        openTooltips.forEach((tooltip) => {
            if (tooltip === this) {
                return;
            }
            const host: Element = tooltip.elementRef.nativeElement;
            if (!host.isConnected || !(target instanceof Node) || !host.contains(target)) {
                tooltip.hide();
            }
        });
    }

    setPositions(): ConnectedPosition[] {
        const top: ConnectedPosition = {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
            panelClass: 'above-panel-tooltip'
        };
        const bottom: ConnectedPosition = {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
            panelClass: 'below-panel-tooltip'
        };
        const left: ConnectedPosition = {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -8,
            panelClass: 'left-panel-tooltip'
        };
        const right: ConnectedPosition = {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8,
            panelClass: 'right-panel-tooltip'
        };
        const belowRight: ConnectedPosition = {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 4,
            panelClass: 'below-right-panel-tooltip'
        };
        const belowLeft: ConnectedPosition = {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 4,
            panelClass: 'below-left-panel-tooltip'
        };
        const aboveRight: ConnectedPosition = {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -4,
            panelClass: 'above-right-panel-tooltip'
        };
        const aboveLeft: ConnectedPosition = {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetY: -4,
            panelClass: 'above-left-panel-tooltip'
        };
        switch (this.tooltipPosition) {
            case 'above':
                return [top, bottom];
                break;
            case 'below':
                return [bottom, top];
                break;
            case 'left':
                return [left, right];
                break;
            case 'right':
                return [right, left];
                break;
            case 'below-right':
                return [belowRight, belowLeft, aboveRight, aboveLeft];
                break;
            case 'below-left':
                return [belowLeft, belowRight, aboveLeft, aboveRight];
                break;
            case 'above-right':
                return [aboveRight, aboveLeft, belowRight, belowLeft];
                break;
            case 'above-left':
                return [aboveLeft, aboveRight, belowLeft, belowRight];
                break;
            default:
                return [top, bottom, aboveLeft, aboveRight, belowLeft, belowRight];
                break;
        }
    }

    ngOnInit() {
        const positions = this.setPositions();
        const positionStrategy = this.overlayPositionBuilder
            // Create position attached to the elementRef
            .flexibleConnectedTo(this.elementRef)
            // Describe how to connect overlay to the elementRef
            // Means, attach overlay's center bottom point to the
            // top center point of the elementRef.
            .withPositions(positions)
            .withTransformOriginOn('.tooltip-container');
        this.overlayRef = this.overlay.create({ positionStrategy });

        /**
         * Last-resort exit for every case where the host never receives an exit event
         *
         * @description `mouseout` travels the ancestor chain of the node the pointer left. When that
         * node has just been removed from the DOM - an `*ngIf` swapping the hovered icon - its chain
         * no longer contains the host, so the host is never notified and the overlay stays attached
         * for good. The `click` exit does not help either: application handlers call
         * `stopImmediatePropagation()` on such icons.
         *
         * `outsidePointerEvents()` is the CDK's own dispatcher. It listens on `document.body` in the
         * CAPTURE phase, so it runs before any application `(click)` handler and cannot be suppressed
         * by one, and it detaches the overlay before the response of a request mutates the row and
         * orphans the hover chain. Its listeners are installed on the first attach and removed when
         * the last overlay detaches, so an idle page keeps none. Because the dispatcher sweeps every
         * attached overlay, one click also clears any tooltip stranded earlier.
         *
         * This requires the pane to stay non-interactive (see show()): the dispatcher stops its sweep
         * at the first overlay containing the clicked target, so a clickable tooltip pane would keep
         * the overlays underneath (dialog, menu, autocomplete) from ever seeing their outside click.
         *
         * No teardown is needed: `OverlayRef.dispose()` completes this subject (see ngOnDestroy).
         */
        this.overlayRef.outsidePointerEvents().subscribe(() => this.hide());
    }

    ngOnDestroy() {
        openTooltips.delete(this);
        stopScrollWatchIfIdle();
        this.overlayRef?.detach();
        this.overlayRef?.dispose();
    }
}
